import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useUser } from '../../context/UserContext';
import {
  sessionsService,
  sessionRegistrationsService,
  progressService,
  coursesService,
  homeworksService,
  homeworkSubmissionsService,
  classroomMissionsService,
  ideaBoardService,
  sideQuestService
} from '../../services/firestoreService';
import { storageService } from '../../services/storageService';
import { activityLogService } from '../../services/activityLogService';
import PointsBadges from './PointsBadges';
import EmojiReactions from './EmojiReactions';
import LivePolls from './LivePolls';
import ClassChat from './ClassChat';
import ProgressDashboard from './ProgressDashboard';
import Leaderboard from './Leaderboard';
import ScreenShare from './ScreenShare';
import TopicInteractiveLearning from './TopicInteractiveLearning';
import CourseVisualLearning from './CourseVisualLearning';
import MissionDeck from './MissionDeck';
import IdeaBoard from './IdeaBoard';
import BrainBreakSpinner from './BrainBreakSpinner';
import SideQuestPanel from './SideQuestPanel';
import LiveCodeClassroom from './LiveCodeClassroom';
import LiveVideoClassroom from './LiveVideoClassroom';
import dailyVideoService from '../../services/dailyVideoService';
import './ClassroomEnhanced.css';

const ClassroomEnhanced = () => {
  const { sessionId } = useParams();
  const { user } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const topicFilter = searchParams.get('topic'); // Get topic from URL parameter
  const [session, setSession] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [registration, setRegistration] = useState(null);
  const [course, setCourse] = useState(null);
  const [homeworks, setHomeworks] = useState([]);
  const [submissions, setSubmissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('video'); // 'video', 'classroom', 'livecode', 'polls', 'chat', 'progress', 'leaderboard'
  const [joinedEarly, setJoinedEarly] = useState(false);
  const [selectedHomework, setSelectedHomework] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [submitting, setSubmitting] = useState(false);
  const [topicProgress, setTopicProgress] = useState({}); // Track progress per topic
  const [missions, setMissions] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [sidequests, setSidequests] = useState([]);
  const backPath = user?.type === 'coach'
    ? '/coach/sessions'
    : user?.type === 'admin'
      ? '/admin/sessions'
      : '/student/registered';
  const canControlLiveCode = user?.type === 'coach' || user?.type === 'admin';

  const handleSubmitHomework = async (homeworkId, answer, files, completed) => {
    setSubmitting(true);
    try {
      if (completed !== undefined) {
        // Update completion status
        const submission = submissions[homeworkId];
        if (submission) {
          await homeworkSubmissionsService.updateSubmission(homeworkId, submission.id, { completed });
          setSubmissions({ ...submissions, [homeworkId]: { ...submission, completed } });
        }
        setSelectedHomework(null);
        return;
      }

      // Log homework submission
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.SUBMIT_HOMEWORK, {
        homeworkId,
        sessionId,
        hasAnswer: !!answer,
        fileCount: files?.length || 0,
        completed
      });
      
      // New submission
      let uploadedFiles = [];
      if (files && files.length > 0) {
        uploadedFiles = await storageService.uploadMultipleFiles(homeworkId, user.id, files);
      }

      await homeworkSubmissionsService.createSubmission(homeworkId, {
        studentId: user.id,
        answer: answer || '',
        files: uploadedFiles,
        completed: false
      });

      // Reload submissions
      const submission = await homeworkSubmissionsService.getStudentSubmission(homeworkId, user.id);
      setSubmissions({ ...submissions, [homeworkId]: submission });
      setSelectedHomework(null);
      alert('Homework submitted successfully!');
    } catch (error) {
      console.error('Error submitting homework:', error);
      alert('Error submitting homework. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    const loadClassroomData = async () => {
      if (!user || !sessionId) return;

      try {
        console.log('🎓 Loading classroom data for session:', sessionId, 'user:', user.id);
        
        // Load session
        let sessionData;
        try {
          sessionData = await sessionsService.getSession(sessionId);
          if (!sessionData) {
            console.error('❌ Session not found:', sessionId);
            setError('Session not found');
            setLoading(false);
            return;
          }
          console.log('✅ Session loaded:', sessionData.title);
        } catch (sessionError) {
          console.error('❌ Error loading session:', sessionError);
          setError(`Error loading session: ${sessionError.message || 'Session not found'}`);
          setLoading(false);
          return;
        }

        // For admins and coaches, skip registration checks - they have direct access
        let registrationData = null;
        if (user.type === 'student') {
          // Load registration - check both registrations and directly assigned sessions
          try {
            registrationData = await sessionRegistrationsService.getStudentRegistration(sessionId, user.id);
            
            // If no registration found, check for directly assigned session
            if (!registrationData) {
              console.log('⚠️ No registration found, checking for directly assigned session...');
              const { studentSessionsService } = await import('../../services/firestoreService');
              const assignedSessions = await studentSessionsService.getStudentSessions(user.id);
              const assignedSession = assignedSessions.find(s => s.sessionId === sessionId);
              
              if (assignedSession && assignedSession.status === 'active') {
                // Create a mock registration object for directly assigned sessions
                registrationData = {
                  id: assignedSession.id,
                  sessionId: assignedSession.sessionId,
                  studentId: user.id,
                  status: 'active',
                  classroomEnabled: true, // Default to enabled for directly assigned sessions
                  assignedAt: assignedSession.assignedAt,
                  assignedBy: assignedSession.assignedBy
                };
                console.log('✅ Found directly assigned session, using mock registration');
              } else {
                console.error('❌ No registration or assignment found');
                setError('You are not registered for this session');
                setLoading(false);
                return;
              }
            } else {
              console.log('✅ Registration found:', registrationData.status);
            }
          } catch (regError) {
            console.error('❌ Error loading registration:', regError);
            setError(`Error loading registration: ${regError.message || 'Registration not found'}`);
            setLoading(false);
            return;
          }

          // Check if registration is approved
          if (registrationData.status !== 'active') {
            console.warn('⚠️ Registration not active:', registrationData.status);
            setError('Your registration is not yet approved. Please wait for admin approval.');
            setLoading(false);
            return;
          }

          // Check if classroom is enabled
          if (!registrationData.classroomEnabled) {
            console.warn('⚠️ Classroom not enabled for this student');
            setError('Classroom access is not enabled for you. Please contact your administrator.');
            setLoading(false);
            return;
          }
        } else {
          // Admin or Coach - create mock registration with full access
          registrationData = {
            id: 'admin-coach-access',
            sessionId: sessionId,
            status: 'active',
            classroomEnabled: true
          };
          console.log('✅ Admin/Coach access granted - skipping registration checks');
        }

        // Load course data if session has a course
        let courseData = null;
        if (sessionData.courseId) {
          try {
            courseData = await coursesService.getCourse(sessionData.courseId);
            console.log('✅ Course loaded:', courseData?.title);
          } catch (courseError) {
            console.warn('⚠️ Could not load course:', courseError);
            // Continue without course data
          }
        }

        // Load homeworks for this session
        let homeworksData = [];
        try {
          const allHomeworks = await homeworksService.getHomeworksBySession(sessionData.id);
          homeworksData = allHomeworks.filter(hw => hw.enabled !== false);
          
          // Load submissions for each homework
          const submissionsPromises = homeworksData.map(async (hw) => {
            const submission = await homeworkSubmissionsService.getStudentSubmission(hw.id, user.id);
            return { homeworkId: hw.id, submission };
          });
          const submissionsResults = await Promise.all(submissionsPromises);
          const submissionsObj = {};
          submissionsResults.forEach(({ homeworkId, submission }) => {
            if (submission) submissionsObj[homeworkId] = submission;
          });
          setSubmissions(submissionsObj);
        } catch (hwError) {
          console.warn('⚠️ Could not load homeworks:', hwError);
        }

        setSession(sessionData);
        setRegistration(registrationData);
        
        // Filter course topics if topicFilter is provided
        if (courseData && topicFilter) {
          const filteredCourse = {
            ...courseData,
            topics: courseData.topics.filter(t => t.name === topicFilter)
          };
          setCourse(filteredCourse);
          console.log(`✅ Course filtered to topic: ${topicFilter}`);
        } else {
          setCourse(courseData);
        }
        
        setHomeworks(homeworksData);

        // Track attendance and check if joined early (within 5 minutes of start)
        try {
          const sessionStart = new Date(sessionData.startDate);
          const now = new Date();
          const minutesUntilStart = (sessionStart - now) / (1000 * 60);
          const early = minutesUntilStart > 0 && minutesUntilStart <= 5;
          setJoinedEarly(early);

          // Track attendance (don't fail if this errors)
          try {
            await progressService.trackSessionAttendance(user.id, sessionId, early);
            console.log('✅ Attendance tracked');
          } catch (attendanceError) {
            console.warn('⚠️ Could not track attendance (non-critical):', attendanceError);
            // Don't fail the whole load if attendance tracking fails
          }
        } catch (attendanceError) {
          console.warn('⚠️ Error in attendance calculation:', attendanceError);
          // Continue anyway
        }
      } catch (err) {
        console.error('❌ Unexpected error loading classroom data:', err);
        console.error('Error details:', {
          message: err.message,
          code: err.code,
          stack: err.stack
        });
        setError(`Error loading classroom: ${err.message || 'Please try again.'}`);
      } finally {
        setLoading(false);
      }
    };

    loadClassroomData();
    
    // Log classroom view
    if (user && sessionId) {
      activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.VIEW_CLASSROOM, {
        sessionId
      });
    }
  }, [user, sessionId, topicFilter]);

  useEffect(() => {
    if (!sessionId) return undefined;

    const unsubscribeMissions = classroomMissionsService.subscribeToMissions(
      sessionId,
      (data) => setMissions(data || []),
      (error) => console.warn('Mission subscription error:', error)
    );

    const unsubscribeIdeas = ideaBoardService.subscribeToIdeas(
      sessionId,
      (data) => setIdeas(data || []),
      (error) => console.warn('Idea board subscription error:', error)
    );

    return () => {
      if (typeof unsubscribeMissions === 'function') unsubscribeMissions();
      if (typeof unsubscribeIdeas === 'function') unsubscribeIdeas();
    };
  }, [sessionId]);

  useEffect(() => {
    if (!user?.id || user.type !== 'student') return undefined;

    const unsubscribe = sideQuestService.subscribeToQuests(
      user.id,
      (data) => setSidequests(data || []),
      (error) => console.warn('Side quest subscription error:', error)
    );

    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [user?.id, user?.type]);

  const handleMissionComplete = async (missionId) => {
    if (!sessionId || !user) return;
    try {
      await classroomMissionsService.logMissionCompletion(sessionId, missionId, user.id);
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.COMPLETE_MISSION, {
        sessionId,
        missionId
      });
    } catch (missionError) {
      console.error('Error completing mission:', missionError);
      alert('Unable to mark mission complete. Please try again.');
    }
  };

  const handleSubmitIdea = async (text, category) => {
    if (!sessionId || !user) return;
    try {
      await ideaBoardService.postIdea(sessionId, {
        authorId: user.id,
        authorName: user.name || 'Learner',
        text,
        category
      });
    } catch (ideaError) {
      console.error('Error posting idea:', ideaError);
      alert('Unable to share idea right now. Please try again.');
    }
  };

  const handleCheerIdea = async (ideaId) => {
    if (!sessionId) return;
    try {
      await ideaBoardService.cheerIdea(sessionId, ideaId);
    } catch (cheerError) {
      console.error('Error cheering idea:', cheerError);
    }
  };

  const handleCompleteSideQuest = async (questId, reward) => {
    if (!user) return;
    try {
      await sideQuestService.completeQuest(user.id, questId, reward);
      await activityLogService.logActivity(user.id, activityLogService.ACTIVITY_TYPES.COMPLETE_SIDEQUEST, {
        questId,
        reward
      });
    } catch (questError) {
      console.error('Error completing side quest:', questError);
      alert('Unable to complete side quest right now.');
    }
  };

  const handleJoinClassroom = () => {
    if (session?.classroomUrl) {
      window.open(session.classroomUrl, '_blank');
    } else {
      alert('Classroom URL is not configured. Please contact your administrator.');
    }
  };

  if (loading) {
    return (
      <div className="classroom-enhanced-container">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading classroom...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="classroom-enhanced-container">
        <div className="error-state">
          <h2>⚠️ Classroom Access</h2>
          <p>{error}</p>
          <button onClick={() => navigate(backPath)} className="back-btn">
            ← Back to Sessions
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="classroom-enhanced-container">
      <div className="classroom-header">
        <button onClick={() => navigate(backPath)} className="back-btn">
          ← Back to Sessions
        </button>
        <div className="header-title">
          <h1>🎓 Live Classroom</h1>
          {topicFilter && (
            <div className="topic-filter-badge">
              <span className="topic-indicator">📚 Topic:</span>
              <span className="topic-name">{topicFilter}</span>
            </div>
          )}
        </div>
        <div className="header-actions">
          <PointsBadges studentId={user.id} />
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="classroom-tabs">
        <button
          className={`tab-btn ${activeTab === 'video' ? 'active' : ''}`}
          onClick={() => setActiveTab('video')}
        >
          🎥 Video Classroom
        </button>
        <button
          className={`tab-btn ${activeTab === 'classroom' ? 'active' : ''}`}
          onClick={() => setActiveTab('classroom')}
        >
          📚 Classroom
        </button>
        <button
          className={`tab-btn ${activeTab === 'livecode' ? 'active' : ''}`}
          onClick={() => setActiveTab('livecode')}
        >
          💻 Live Code
        </button>
        <button
          className={`tab-btn ${activeTab === 'polls' ? 'active' : ''}`}
          onClick={() => setActiveTab('polls')}
        >
          📊 Polls & Quizzes
        </button>
        <button
          className={`tab-btn ${activeTab === 'chat' ? 'active' : ''}`}
          onClick={() => setActiveTab('chat')}
        >
          💬 Chat & Q&A
        </button>
        <button
          className={`tab-btn ${activeTab === 'progress' ? 'active' : ''}`}
          onClick={() => setActiveTab('progress')}
        >
          📈 My Progress
        </button>
        <button
          className={`tab-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
        >
          🏆 Leaderboard
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'video' && (
          <div className="video-classroom-container">
            {dailyVideoService.isConfigured() ? (
              <LiveVideoClassroom
                sessionId={sessionId}
                userId={user?.id || user?.uid}
                userName={user?.name || user?.displayName || user?.email || 'Instructor'}
                userType={user?.type || 'admin'}
                isInstructor={user?.type === 'coach' || user?.type === 'admin'}
              />
            ) : (
              <div className="classroom-info">
                <div className="session-card-classroom">
                  <h2>{session?.title}</h2>
                  <div className="session-details">
                    <p><strong>📅 Start:</strong> {session?.startDate ? new Date(session.startDate).toLocaleDateString() : 'N/A'}</p>
                    <p><strong>📅 End:</strong> {session?.endDate ? new Date(session.endDate).toLocaleDateString() : 'N/A'}</p>
                    {session?.classroomUrl ? (
                      <div className="join-section">
                        <h3>🚀 Ready to Join?</h3>
                        <p>Click the button below to join the live classroom session.</p>
                        <button onClick={() => window.open(session.classroomUrl, '_blank')} className="join-classroom-btn">
                          🎥 Join Live Classroom
                        </button>
                      </div>
                    ) : (
                      <div className="no-url-section">
                        <p>⚠️ Video classroom is not configured. Please set up Daily.co API key or add a classroom URL.</p>
                        <p style={{ marginTop: '10px', fontSize: '0.9rem', color: '#666' }}>
                          Make sure REACT_APP_DAILY_API_KEY is set in your .env file and restart the development server.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
        {activeTab === 'classroom' && (
          <div className="classroom-main">
            <div className="session-card-classroom">
              <h2>{session.title}</h2>
              <div className="session-details">
                <p><strong>📅 Start:</strong> {new Date(session.startDate).toLocaleDateString()}</p>
                <p><strong>📅 End:</strong> {new Date(session.endDate).toLocaleDateString()}</p>
                <p><strong>⏱️ Duration:</strong> {session.totalDuration} hours</p>
                {session.course && (
                  <p><strong>📚 Course:</strong> {session.course.title}</p>
                )}
              </div>
            </div>

            <div className="classroom-access">
              <div className="access-status">
                <div className="status-badge enabled">
                  ✅ Classroom Access Enabled
                </div>
                {joinedEarly && (
                  <div className="early-badge">
                    🏆 You joined early! +10 bonus points!
                  </div>
                )}
              </div>

              {session.classroomUrl ? (
                <div className="join-section">
                  <h3>🚀 Ready to Join?</h3>
                  <p>Click the button below to join the live classroom session.</p>
                  <button onClick={handleJoinClassroom} className="join-classroom-btn">
                    🎥 Join Live Classroom
                  </button>
                  <p className="classroom-url">
                    <strong>Classroom URL:</strong> 
                    <a href={session.classroomUrl} target="_blank" rel="noopener noreferrer">
                      {session.classroomUrl}
                    </a>
                  </p>
                </div>
              ) : (
                <div className="no-url-section">
                  <p>⚠️ Classroom URL is being configured. Please check back soon or contact your instructor.</p>
                </div>
              )}
            </div>

            {/* Screen Sharing */}
            <div className="screen-share-section">
              <ScreenShare
                sessionId={sessionId}
                userId={user.id}
                userName={user.name}
                userType={user.type}
              />
            </div>

            {/* Overall Course Progress */}
            {course && course.topics && course.topics.length > 0 && (
              <div className="course-progress-section">
                <h3>📊 Overall Course Progress</h3>
                <div className="course-progress-card">
                  <div className="progress-header">
                    <h4>{course.title}</h4>
                    <span className="progress-percentage">
                      {Math.round(
                        (Object.keys(topicProgress).filter(
                          topicName => topicProgress[topicName]?.completed
                        ).length / course.topics.length) * 100
                      )}%
                    </span>
                  </div>
                  <div className="progress-bar-overall">
                    <div 
                      className="progress-bar-fill-overall"
                      style={{
                        width: `${(Object.keys(topicProgress).filter(
                          topicName => topicProgress[topicName]?.completed
                        ).length / course.topics.length) * 100}%`
                      }}
                    />
                  </div>
                  <div className="progress-stats">
                    <div className="stat-item">
                      <span className="stat-label">Topics Completed:</span>
                      <span className="stat-value">
                        {Object.keys(topicProgress).filter(
                          topicName => topicProgress[topicName]?.completed
                        ).length} / {course.topics.length}
                      </span>
                    </div>
                    <div className="stat-item">
                      <span className="stat-label">Total Stars:</span>
                      <span className="stat-value">
                        ⭐ {Object.values(topicProgress).reduce(
                          (sum, progress) => sum + (progress?.stars || 0), 0
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="topics-progress-list">
                    {course.topics.map((topic, idx) => {
                      const progress = topicProgress[topic.name] || { completed: false, stars: 0 };
                      return (
                        <div key={idx} className={`topic-progress-item ${progress.completed ? 'completed' : ''}`}>
                          <span className="topic-progress-name">{topic.name}</span>
                          <div className="topic-progress-indicator">
                            {progress.completed ? (
                              <span className="progress-check">✅</span>
                            ) : (
                              <span className="progress-pending">⏳</span>
                            )}
                            {progress.stars > 0 && (
                              <span className="topic-stars">⭐ {progress.stars}</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Course Visual Learning Experience - Progressive Visual Learning */}
            {course && course.topics && course.topics.length > 0 && (
              <div className="course-visual-learning-wrapper">
                <CourseVisualLearning
                  courseId={session?.courseId}
                  sessionId={sessionId}
                  userId={user.id}
                  userName={user.name}
                />
              </div>
            )}

            {/* Interactive Classroom Widgets */}
            <div className="classroom-grid-panels">
              <MissionDeck
                missions={missions}
                onComplete={handleMissionComplete}
                userType={user.type}
              />
              <IdeaBoard
                ideas={ideas}
                onSubmitIdea={handleSubmitIdea}
                onCheerIdea={handleCheerIdea}
                user={user}
              />
              <BrainBreakSpinner />
              {user.type === 'student' && (
                <SideQuestPanel
                  quests={sidequests}
                  onCompleteQuest={handleCompleteSideQuest}
                  user={user}
                />
              )}
            </div>

            {/* Course Topics with Sample Code */}
            {course && course.topics && course.topics.length > 0 && (
              <div className="course-topics-section">
                <h3>📚 Course Topics & Sample Code</h3>
                <div className="topics-list-classroom">
                  {course.topics.map((topic, idx) => (
                    <div key={idx} className="topic-card-classroom">
                      <div className="topic-header-classroom">
                        <h4>{topic.name}</h4>
                        {topic.hours && (
                          <span className="topic-hours-badge">{topic.hours}h</span>
                        )}
                      </div>
                      {topic.description && (
                        <p className="topic-description-classroom">{topic.description}</p>
                      )}
                      {topic.sampleCode && (
                        <div className="sample-code-display">
                          <div className="code-header">
                            <span>💻 Sample Code</span>
                            <button
                              className="copy-code-btn"
                              onClick={() => {
                                navigator.clipboard.writeText(topic.sampleCode);
                                alert('Code copied to clipboard!');
                              }}
                            >
                              📋 Copy
                            </button>
                          </div>
                          <pre className="code-block">
                            <code>{topic.sampleCode}</code>
                          </pre>
                        </div>
                      )}
                      
                      {/* Interactive Learning for this topic */}
                      <TopicInteractiveLearning
                        userId={user.id}
                        userName={user.name}
                        topic={topic}
                        courseId={session?.courseId}
                        onProgressUpdate={(stars) => {
                          setTopicProgress(prev => ({
                            ...prev,
                            [topic.name]: {
                              completed: true,
                              stars: (prev[topic.name]?.stars || 0) + stars
                            }
                          }));
                        }}
                      />

                      {/* Homework for this topic */}
                      {homeworks.filter(hw => hw.topicId === topic.name).length > 0 && (
                        <div className="topic-homework-section">
                          <h5>📝 Homework</h5>
                          {homeworks.filter(hw => hw.topicId === topic.name).map((homework) => {
                            const submission = submissions[homework.id];
                            return (
                              <div key={homework.id} className="homework-item-classroom">
                                <div className="homework-header-classroom">
                                  <strong>{homework.title}</strong>
                                  <span className="due-date-badge">
                                    Due: {new Date(homework.dueDate).toLocaleDateString()}
                                  </span>
                                </div>
                                {homework.description && (
                                  <p className="homework-desc-classroom">{homework.description}</p>
                                )}
                                {submission ? (
                                  <div className="submission-status-classroom">
                                    <span className="submitted-badge-classroom">✅ Submitted</span>
                                    <span className="submission-date-classroom">
                                      {new Date(submission.submittedAt).toLocaleString()}
                                    </span>
                                    {submission.completed && (
                                      <span className="completed-badge">✓ Completed</span>
                                    )}
                                    <button
                                      className="view-submission-btn"
                                      onClick={() => setSelectedHomework({ ...homework, submission })}
                                    >
                                      View Submission
                                    </button>
                                  </div>
                                ) : (
                                  <button
                                    className="submit-homework-btn"
                                    onClick={() => setSelectedHomework(homework)}
                                  >
                                    📤 Submit Homework
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Emoji Reactions */}
            <EmojiReactions sessionId={sessionId} studentId={user.id} />

            <div className="classroom-instructions">
              <h3>📋 Instructions</h3>
              <ul>
                <li>Make sure you have a stable internet connection</li>
                <li>Use headphones or earphones for better audio quality</li>
                <li>Join a few minutes before the scheduled time</li>
                <li>Keep your microphone muted when not speaking</li>
                <li>Raise your hand or use chat to ask questions</li>
                <li>If you experience technical issues, contact your instructor</li>
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'livecode' && (
          <LiveCodeClassroom sessionId={sessionId} user={user} isCoach={canControlLiveCode} />
        )}

        {activeTab === 'polls' && (
          <LivePolls sessionId={sessionId} studentId={user.id} />
        )}

        {activeTab === 'chat' && (
          <ClassChat sessionId={sessionId} studentId={user.id} userName={user.name} />
        )}

        {activeTab === 'progress' && (
          <ProgressDashboard studentId={user.id} sessionId={sessionId} />
        )}

        {activeTab === 'leaderboard' && (
          <Leaderboard />
        )}
      </div>

      {/* Homework Submission Modal */}
      {selectedHomework && (
        <HomeworkSubmissionModal
          homework={selectedHomework}
          onClose={() => setSelectedHomework(null)}
          onSubmit={handleSubmitHomework}
        />
      )}
    </div>
  );
};

const HomeworkSubmissionModal = ({ homework, onClose, onSubmit }) => {
  const [answer, setAnswer] = useState('');
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [completed, setCompleted] = useState(homework.submission?.completed || false);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const allowedTypes = ['.doc', '.docx', '.pdf', '.zip'];
    const validFiles = selectedFiles.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      return allowedTypes.includes(ext);
    });
    setFiles(validFiles);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (homework.submission) {
      // Viewing existing submission - allow marking as completed
      if (completed !== homework.submission.completed) {
        await onSubmit(homework.id, null, null, completed);
      }
      onClose();
      return;
    }
    
    if (!answer.trim() && files.length === 0) {
      alert('Please provide an answer or attach a file.');
      return;
    }
    setUploading(true);
    try {
      await onSubmit(homework.id, answer, files, false);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{homework.submission ? 'View Submission' : 'Submit Homework'}: {homework.title}</h2>
        {homework.submission ? (
          <div className="submission-view">
            <div className="form-group">
              <label>Your Answer</label>
              <div className="submission-answer-display">{homework.submission.answer || 'No text answer provided'}</div>
            </div>
            {homework.submission.files && homework.submission.files.length > 0 && (
              <div className="form-group">
                <label>Attached Files</label>
                <div className="files-list">
                  {homework.submission.files.map((file, idx) => (
                    <a key={idx} href={file.url} target="_blank" rel="noopener noreferrer" className="file-link">
                      📎 {file.name}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  checked={completed}
                  onChange={(e) => setCompleted(e.target.checked)}
                />
                Mark as Completed
              </label>
            </div>
            {homework.submission.grade !== undefined && (
              <div className="form-group">
                <label>Grade</label>
                <div className="grade-display">{homework.submission.grade} / 100</div>
              </div>
            )}
            {homework.submission.feedback && (
              <div className="form-group">
                <label>Feedback</label>
                <div className="feedback-display">{homework.submission.feedback}</div>
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={handleSubmit} className="save-btn" disabled={uploading}>
                {uploading ? 'Saving...' : 'Save Changes'}
              </button>
              <button type="button" onClick={onClose} className="cancel-btn">
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label>Your Answer *</label>
              <textarea
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                rows="6"
                placeholder="Enter your answer here..."
                required={files.length === 0}
              />
            </div>
            <div className="form-group">
              <label>Attach Files (Word, PDF, ZIP)</label>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".doc,.docx,.pdf,.zip"
              />
              {files.length > 0 && (
                <div className="files-list">
                  {files.map((file, idx) => (
                    <span key={idx} className="file-tag">
                      {file.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="modal-actions">
              <button type="button" onClick={onClose} className="cancel-btn">
                Cancel
              </button>
              <button type="submit" className="submit-btn" disabled={uploading}>
                {uploading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ClassroomEnhanced;

